import {
  Box,
  Flex,
  Image,
  Stack,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';

import { DashboardWorkshop } from '@/client/types/DashboardWorkshop';
import LearnerDashboardCustomTab from '@/client/components/learners/dashboard/components/tabs/LearnerDashboardCustomTab';
import LearnerDashboardNoDataPlaceholder from '../components/LearnerDashboardNoDataPlaceholder';
import { format } from 'date-fns';
import { getMSWIdFromSessionId } from '@/client/services/api/content/workshops/MultiSessionWorkshopService';
import { useAuthStore } from '@/client/services/state/authStore';
import { useGetUserWorkshops } from '@/client/components/learners/dashboard/hooks/useGetUserWorkshops';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function NoWorkshopsPlaceholder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authConfig } = useAuthStore();
  const { company } = authConfig;

  const dedicatedWorkshopsEnabled = company.feature_flags.enable_dedicated_workshops;

  return (
    <LearnerDashboardNoDataPlaceholder
      imagePath="/public/images/Hiring-1--Streamline-Brooklyn.svg"
      title={t('learners.dashboard.workshops.noWorksops.title')}
      helpText={t('learners.dashboard.workshops.noWorksops.helpText')}
      buttonData={{
        buttonCopy: t('learners.dashboard.workshops.noWorksops.buttonCopy'),
        buttonAction: () => {
          if (dedicatedWorkshopsEnabled) {
            navigate('/workshops');
          } else {
            navigate('/explore');
          }
        },
      }}
    />
  );
}

function WorkshopCard({ workshopItem }: { workshopItem: DashboardWorkshop }) {
  const navigate = useNavigate();

  const { workshop, workshop_instance: workshopInstance } = workshopItem;

  // Add null check before destructuring
  if (!workshop) {
    return null; // or return a placeholder component
  }

  const { type, _id: id, banner_image: bannerImage, title } = workshop;

  const { _id: workshopInstanceId } = workshopInstance;

  const setWorkshopUrl = async () => {
    let contentRoute = '';

    if (type === 'multi_session_workshop_session') {
      const mswId = await getMSWIdFromSessionId(id);

      contentRoute = `/multi-session-workshop/${mswId}`;
    } else if (type === 'multi_session_workshop') {
      contentRoute = `/multi-session-workshop/${id}`;
    } else if (type === 'imported_content') {
      contentRoute = `/imported-content/${id}`;
    } else if (type === 'badge') {
      contentRoute = '';
    } else if (type === 'workshop' && workshopInstanceId) {
      contentRoute = `/workshop/${id}?workshopInstanceId=${workshopInstanceId}`;
    }

    navigate(contentRoute);
  };

  return (
    <Flex
      paddingY={4}
      paddingX={6}
      backgroundColor="white"
      border="1px solid"
      borderColor="neutral.100"
      borderRadius="16px"
      alignItems="center"
      transition="background-color 400ms"
      _hover={{ cursor: 'pointer', backgroundColor: 'neutral.100' }}
      onClick={setWorkshopUrl}
    >
      <VStack spacing={0} borderRight="1px solid" borderColor="neutral.100" paddingRight={6}>
        <Text fontSize="14px" fontWeight={600} color="neutral.1300">
          {format(new Date(workshopInstance.start_date), 'MMM')}
        </Text>

        <Text fontSize="20px" fontWeight={600} color="neutral.1300">
          {format(new Date(workshopInstance.start_date), 'dd')}
        </Text>

        <Text fontSize="12px" fontWeight={500} color="neutral.800">
          {format(new Date(workshopInstance.start_date), 'h:mm a')}
        </Text>
      </VStack>

      <Stack
        direction={{ base: 'column', md: 'row' }}
        alignItems={{ base: 'flex-start', md: 'center' }}
        marginLeft={6}
        spacing={4}
      >
        <Image
          width="65px"
          height="35px"
          borderRadius="4px"
          backgroundColor="brand.primary"
          src={bannerImage}
        />

        <Text fontSize="16px" fontWeight={500}>
          {title}
        </Text>
      </Stack>
    </Flex>
  );
}

export default function LearnerDashboardWorkshops() {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<'upcoming' | 'past'>('upcoming');

  const selectedCountStyles = {
    marginLeft: 2,
    borderRadius: 'full',
    backgroundColor: 'whiteAlpha.500',
    paddingX: 2,
    paddingY: 1,
    fontSize: '10px',
    fontWeight: 600,
  };

  const unselectedCountStyles = {
    marginLeft: 2,
    borderRadius: 'full',
    backgroundColor: 'neutral.100',
    paddingX: 2,
    paddingY: 1,
    fontSize: '10px',
    fontWeight: 600,
    color: 'neutral.700',
  };

  const { isLoading, isError, data } = useGetUserWorkshops();

  if (isLoading) return <div>Loading...</div>;

  if (isError) return <div>Error</div>;

  const { attended: pastWorkshops, registered: upcomingWorkshops } = data;

  const groupWorkshopsByMonth = (workshops: DashboardWorkshop[]) =>
    workshops.reduce((groups, workshop) => {
      const date = new Date(workshop.workshop_instance.start_date);
      const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });

      if (!groups[monthYear]) {
        Object.assign(groups, { [monthYear]: [] });
      }

      groups[monthYear].push(workshop);
      return groups;
    }, {} as Record<string, DashboardWorkshop[]>);

  const sortWorkshopsInGroup = (workshops: DashboardWorkshop[]) =>
    [...workshops].sort((a, b) => {
      const dateA = new Date(a.workshop_instance.start_date);
      const dateB = new Date(b.workshop_instance.start_date);

      // First sort by day
      if (dateA.getDate() !== dateB.getDate()) {
        return dateA.getDate() - dateB.getDate();
      }

      // If same day, sort by time
      return dateA.getTime() - dateB.getTime();
    });

  const groupedPastWorkshops = groupWorkshopsByMonth(pastWorkshops);
  const groupedUpcomingWorkshops = groupWorkshopsByMonth(upcomingWorkshops);

  // Sort workshops within each month group
  Object.keys(groupedPastWorkshops).forEach((monthYear) => {
    groupedPastWorkshops[monthYear] = sortWorkshopsInGroup(groupedPastWorkshops[monthYear]);
  });

  Object.keys(groupedUpcomingWorkshops).forEach((monthYear) => {
    groupedUpcomingWorkshops[monthYear] = sortWorkshopsInGroup(groupedUpcomingWorkshops[monthYear]);
  });

  return (
    <Tabs>
      <Box
        position="sticky"
        top={0}
        zIndex={1}
        paddingY={4}
        paddingX={{ base: 4, md: 0 }}
        backgroundColor="white"
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        width="full"
      >
        <TabList>
          <LearnerDashboardCustomTab
            display="flex"
            alignItems="center"
            onClick={() => setSelectedTab('upcoming')}
          >
            {t('learners.dashboard.workshops.upcoming')}{' '}
            <Box sx={selectedTab === 'upcoming' ? selectedCountStyles : unselectedCountStyles}>
              {upcomingWorkshops.length}
            </Box>
          </LearnerDashboardCustomTab>

          <LearnerDashboardCustomTab
            display="flex"
            alignItems="center"
            onClick={() => setSelectedTab('past')}
          >
            {t('learners.dashboard.workshops.past')}{' '}
            <Box sx={selectedTab === 'past' ? selectedCountStyles : unselectedCountStyles}>
              {pastWorkshops.length}
            </Box>
          </LearnerDashboardCustomTab>
        </TabList>
      </Box>

      <TabPanels>
        <TabPanel paddingX={0}>
          <Stack spacing={6}>
            {upcomingWorkshops.length > 0 ? (
              Object.entries(groupedUpcomingWorkshops).map(([monthYear, workshops]) => (
                <Stack key={monthYear} spacing={4}>
                  {workshops.map((workshop) => (
                    <WorkshopCard key={workshop._id} workshopItem={workshop} />
                  ))}
                </Stack>
              ))
            ) : (
              <NoWorkshopsPlaceholder />
            )}
          </Stack>
        </TabPanel>

        <TabPanel paddingX={0}>
          <Stack spacing={6}>
            {pastWorkshops.length > 0 ? (
              Object.entries(groupedPastWorkshops).map(([monthYear, workshops]) => (
                <Stack key={monthYear} spacing={4}>
                  {workshops.map((workshop) => (
                    <WorkshopCard key={workshop._id} workshopItem={workshop} />
                  ))}
                </Stack>
              ))
            ) : (
              <NoWorkshopsPlaceholder />
            )}
          </Stack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}
